import { supabase } from './supabase';
import type { 
  Screen, 
  ScreenCommand, 
  ScreenCommandType, 
  ScreenMode, 
  ScreenGroup, 
  ScreenPreset,
  ScreenRegistration 
} from '../types/screens';

// ============================================
// DEVICE ID MANAGEMENT
// ============================================

const DEVICE_ID_KEY = 'fcc_device_id';

export function getOrCreateDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

// ============================================
// SCREEN REGISTRATION
// ============================================

export async function registerScreen(registration: ScreenRegistration): Promise<Screen | null> {
  const { data, error } = await supabase
    .from('screens')
    .upsert({
      device_id: registration.deviceId,
      household_id: registration.householdId,
      name: registration.name,
      location: registration.location,
      is_online: true,
      last_seen_at: new Date().toISOString(),
    }, {
      onConflict: 'device_id',
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to register screen:', error);
    return null;
  }

  return data;
}

export async function getScreenByDeviceId(deviceId: string): Promise<Screen | null> {
  const { data } = await supabase
    .from('screens')
    .select('*')
    .eq('device_id', deviceId)
    .single();

  return data;
}

// ============================================
// HEARTBEAT (keep screen online)
// ============================================

export async function sendHeartbeat(screenId: string, currentState: {
  current_mode: ScreenMode;
  is_screensaver_active: boolean;
  brightness: number;
}): Promise<void> {
  await supabase
    .from('screens')
    .update({
      is_online: true,
      last_seen_at: new Date().toISOString(),
      ...currentState,
    })
    .eq('id', screenId);
}

// Start heartbeat interval (call on screen mount)
export function startHeartbeat(
  screenId: string,
  getCurrentState: () => { current_mode: ScreenMode; is_screensaver_active: boolean; brightness: number },
  intervalMs: number = 30000
): () => void {
  const interval = setInterval(() => {
    sendHeartbeat(screenId, getCurrentState());
  }, intervalMs);

  // Send initial heartbeat
  sendHeartbeat(screenId, getCurrentState());

  // Return cleanup function
  return () => clearInterval(interval);
}

// ============================================
// COMMAND HANDLING
// ============================================

export async function getPendingCommands(screenId: string): Promise<ScreenCommand[]> {
  const { data } = await supabase
    .from('screen_commands')
    .select('*')
    .eq('screen_id', screenId)
    .is('executed_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at');

  return data || [];
}

export async function acknowledgeCommand(commandId: string): Promise<void> {
  await supabase
    .from('screen_commands')
    .update({
      executed_at: new Date().toISOString(),
      acknowledged_at: new Date().toISOString(),
    })
    .eq('id', commandId);
}

// Subscribe to commands for a screen (realtime)
export function subscribeToCommands(
  screenId: string,
  onCommand: (command: ScreenCommand) => void
): () => void {
  const channel = supabase
    .channel(`screen-commands-${screenId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'screen_commands',
        filter: `screen_id=eq.${screenId}`,
      },
      (payload) => {
        onCommand(payload.new as ScreenCommand);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================
// SEND COMMANDS (from admin/phone)
// ============================================

export async function sendCommand(
  screenId: string,
  commandType: ScreenCommandType,
  commandData: Record<string, unknown> = {},
  sentBy?: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from('screen_commands')
    .insert({
      screen_id: screenId,
      command_type: commandType,
      command_data: commandData,
      sent_by: sentBy,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to send command:', error);
    return null;
  }

  return data.id;
}

export async function sendCommandToMultiple(
  screenIds: string[],
  commandType: ScreenCommandType,
  commandData: Record<string, unknown> = {},
  sentBy?: string
): Promise<number> {
  const commands = screenIds.map(screenId => ({
    screen_id: screenId,
    command_type: commandType,
    command_data: commandData,
    sent_by: sentBy,
  }));

  const { error } = await supabase
    .from('screen_commands')
    .insert(commands);

  if (error) {
    console.error('Failed to send commands:', error);
    return 0;
  }

  return screenIds.length;
}

export async function sendCommandToAll(
  householdId: string,
  commandType: ScreenCommandType,
  commandData: Record<string, unknown> = {},
  sentBy?: string
): Promise<number> {
  // Get all online screens
  const { data: screens } = await supabase
    .from('screens')
    .select('id')
    .eq('household_id', householdId)
    .eq('is_online', true);

  if (!screens || screens.length === 0) return 0;

  return sendCommandToMultiple(
    screens.map(s => s.id),
    commandType,
    commandData,
    sentBy
  );
}

// Convenience functions
export const changeScreenMode = (screenId: string, mode: ScreenMode, sentBy?: string) =>
  sendCommand(screenId, 'change_mode', { mode }, sentBy);

export const wakeScreen = (screenId: string, sentBy?: string) =>
  sendCommand(screenId, 'wake', {}, sentBy);

export const sleepScreen = (screenId: string, sentBy?: string) =>
  sendCommand(screenId, 'sleep', {}, sentBy);

export const setScreenBrightness = (screenId: string, brightness: number, sentBy?: string) =>
  sendCommand(screenId, 'set_brightness', { brightness }, sentBy);

export const refreshScreen = (screenId: string, sentBy?: string) =>
  sendCommand(screenId, 'refresh', {}, sentBy);

export const pushAnnouncementToScreen = (screenId: string, announcementId: string, durationSeconds?: number, sentBy?: string) =>
  sendCommand(screenId, 'show_announcement', { announcement_id: announcementId, duration_seconds: durationSeconds }, sentBy);

export const pushPhotoToScreen = (screenId: string, photoUrl: string, durationSeconds?: number, sentBy?: string) =>
  sendCommand(screenId, 'show_photo', { photo_url: photoUrl, duration_seconds: durationSeconds }, sentBy);

// ============================================
// SCREEN MANAGEMENT (CRUD)
// ============================================

export async function getAllScreens(householdId: string): Promise<Screen[]> {
  const { data } = await supabase
    .from('screens')
    .select('*')
    .eq('household_id', householdId)
    .order('sort_order');

  return data || [];
}

export async function updateScreen(screenId: string, updates: Partial<Screen>): Promise<Screen | null> {
  const { data, error } = await supabase
    .from('screens')
    .update(updates)
    .eq('id', screenId)
    .select()
    .single();

  if (error) {
    console.error('Failed to update screen:', error);
    return null;
  }

  return data;
}

export async function deleteScreen(screenId: string): Promise<boolean> {
  const { error } = await supabase
    .from('screens')
    .delete()
    .eq('id', screenId);

  return !error;
}

// ============================================
// SCREEN GROUPS
// ============================================

export async function getScreenGroups(householdId: string): Promise<ScreenGroup[]> {
  const { data } = await supabase
    .from('screen_groups')
    .select('*')
    .eq('household_id', householdId);

  return data || [];
}

export async function createScreenGroup(
  householdId: string,
  name: string,
  screenIds: string[],
  icon?: string
): Promise<ScreenGroup | null> {
  const { data, error } = await supabase
    .from('screen_groups')
    .insert({
      household_id: householdId,
      name,
      screen_ids: screenIds,
      icon: icon || '📺',
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create group:', error);
    return null;
  }

  return data;
}

export async function sendCommandToGroup(
  groupId: string,
  commandType: ScreenCommandType,
  commandData: Record<string, unknown> = {},
  sentBy?: string
): Promise<number> {
  const { data: group } = await supabase
    .from('screen_groups')
    .select('screen_ids')
    .eq('id', groupId)
    .single();

  if (!group) return 0;

  return sendCommandToMultiple(group.screen_ids, commandType, commandData, sentBy);
}

// ============================================
// PRESETS
// ============================================

export async function getScreenPresets(householdId: string): Promise<ScreenPreset[]> {
  const { data } = await supabase
    .from('screen_presets')
    .select('*')
    .eq('household_id', householdId);

  return data || [];
}

export async function createScreenPreset(
  householdId: string,
  name: string,
  screenConfigs: Record<string, { mode?: ScreenMode; brightness?: number }>,
  icon?: string
): Promise<ScreenPreset | null> {
  const { data, error } = await supabase
    .from('screen_presets')
    .insert({
      household_id: householdId,
      name,
      screen_configs: screenConfigs,
      icon: icon || '⚡',
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create preset:', error);
    return null;
  }

  return data;
}

export async function applyPreset(presetId: string, sentBy?: string): Promise<number> {
  const { data: preset } = await supabase
    .from('screen_presets')
    .select('screen_configs')
    .eq('id', presetId)
    .single();

  if (!preset) return 0;

  let count = 0;
  for (const [screenId, config] of Object.entries(preset.screen_configs)) {
    const cfg = config as { mode?: ScreenMode; brightness?: number };
    
    if (cfg.mode) {
      await sendCommand(screenId, 'change_mode', { mode: cfg.mode }, sentBy);
      count++;
    }
    if (cfg.brightness !== undefined) {
      await sendCommand(screenId, 'set_brightness', { brightness: cfg.brightness }, sentBy);
    }
  }

  return count;
}

// ============================================
// SUBSCRIBE TO SCREEN STATUS CHANGES
// ============================================

export function subscribeToScreenStatus(
  householdId: string,
  onUpdate: (screens: Screen[]) => void
): () => void {
  // Initial fetch
  getAllScreens(householdId).then(onUpdate);

  const channel = supabase
    .channel(`screens-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'screens',
        filter: `household_id=eq.${householdId}`,
      },
      () => {
        // Refetch all screens on any change
        getAllScreens(householdId).then(onUpdate);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
